import { ADALONG_COLORS } from '@src/styles';
import type { ResGetSocialTopProducts } from 'common/dist/types/apiTypes';
import { stylesheet } from 'typestyle';

interface Props {
  data?: ResGetSocialTopProducts;
  isLoading: boolean;
  isError: boolean;
}

export function TopSocialProducts({ data, isLoading, isError }: Props) {
  return (
    <section className={sheet.panel} aria-label="Top 10 Products “Hot on Social”">
      <div className={sheet.header}>
        <div>
          <h2 className={sheet.title}>Top 10 Products “Hot on Social”</h2>
        </div>
      </div>
      {isLoading && <p className={sheet.emptyState}>Loading product analytics…</p>}
      {isError && <p className={sheet.emptyState}>Unable to load product analytics.</p>}
      {!isLoading && !isError && data?.products.length === 0 && (
        <p className={sheet.emptyState}>No shoppable UGC in this period.</p>
      )}
      {!isLoading && !isError && data && data.products.length > 0 && (
        <ol className={sheet.productList}>
          {data.products.map((product) => (
            <li key={product.productId} className={sheet.product}>
              <div className={sheet.productVisual}>
                {product.imageLink ? (
                  <img className={sheet.productImage} src={product.imageLink} alt="" />
                ) : <div className={sheet.productPlaceholder} aria-hidden="true" />}
                <span className={sheet.productCount}>{product.ugcCount}</span>
              </div>
              <div className={sheet.productDetails}>
                <span className={sheet.productTitle}>{product.title}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

const sheet = stylesheet({
  panel: {
    // Match the embedded dashboard's 24px side padding and 1048px grid limit.
    boxSizing: 'border-box',
    width: 'calc(100% - 48px)',
    maxWidth: 1048,
    margin: '0 auto',
    padding: 24,
    border: '1px solid rgba(255, 255, 255, 0.21)',
    borderRadius: 12,
    backgroundColor: ADALONG_COLORS.DARK_BLACK,
  },
  header: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  eyebrow: {
    margin: 0,
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  title: {
    margin: '5px 0 0',
    color: ADALONG_COLORS.LIGHT_WHITE,
    fontSize: 18,
    fontWeight: 600,
  },
  emptyState: {
    margin: '18px 0 0',
    color: ADALONG_COLORS.LIGHT_GRAY,
    fontSize: 14,
  },
  productList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
    margin: '18px 0 0',
    padding: 0,
    listStyle: 'none',
    $nest: {
      '@media (max-width: 760px)': { gridTemplateColumns: '1fr' },
    },
  },
  product: {
    display: 'grid',
    gridTemplateColumns: '76px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 16,
  },
  productVisual: { position: 'relative', width: 76, height: 76 },
  productImage: {
    display: 'block',
    width: 76,
    height: 76,
    borderRadius: 10,
    objectFit: 'cover',
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
  },
  productPlaceholder: {
    width: 76,
    height: 76,
    borderRadius: 10,
    backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
  },
  productCount: {
    position: 'absolute',
    top: -8,
    right: -8,
    display: 'grid',
    minWidth: 30,
    height: 30,
    padding: '0 7px',
    borderRadius: 15,
    placeItems: 'center',
    backgroundColor: ADALONG_COLORS.LIGHT_YELLOW,
    color: ADALONG_COLORS.DARK_BLACK,
    fontSize: 14,
    fontWeight: 600,
  },
  productDetails: { display: 'grid', gap: 4, minWidth: 0 },
  productTitle: {
    color: ADALONG_COLORS.LIGHT_WHITE,
    fontSize: 16,
    fontWeight: 600,
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  },
});
